import { t } from "@lingui/core/macro";
import {
  ComparatorResult,
  downloadTextAsFile,
  EMPTY_ARRAY,
  EMPTY_STRING,
  stringComparator,
  stringEllipsize,
} from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  ConditionOperator,
  CreateFieldInstanceValueInputs,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldInstanceValueInputType,
  PropertyDataType,
  PropertyTypeFields,
  SpectrumFieldVersionFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  UpdateStartingConditionsInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import {
  convertRichTextToPlainText,
  RegrelloButton,
  RegrelloCallout,
  RegrelloClampedText,
  RegrelloIcon,
  RegrelloLinkify,
  RegrelloTooltippedText,
} from "@regrello/ui-core";
import type { FieldPath, FieldValues } from "react-hook-form";
import { MAX_TEXT_FIELD_VALUE_CHARS, WORKFLOW_END_REASON_FIELD_NAME } from "../../../../../constants/globalConstants";
import type { FieldInstanceBaseFields } from "../../../../../types";
import { getFieldInstanceId } from "../../../../../utils/customFields/getFieldInstanceId";
import {
  getRegrelloDefaultFilterDefinitionStringValue,
  getRegrelloFilterDefinitionStringValue,
} from "../../../../../utils/filters/v2/filterKinds/core/filterString";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import { TableCellDefaultWidths } from "../../../../../utils/tableCellWidthUtils";
import { RegrelloControlledFormFieldText } from "../../formFields/controlled/RegrelloControlledFormFieldText";
import { RegrelloControlledFormFieldTextMultiline } from "../../formFields/controlled/RegrelloControlledFormFieldTextMultiline";
import { FrontendValueConstraintRuleName } from "../../spectrumFields/utils/spectrumFieldConstraintUtils";
import { RegrelloCustomFieldMultiValuePopover } from "../components/RegrelloCustomFieldMultiValuePopover";
import { CustomFieldPluginRegistrar } from "./registry/customFieldPluginRegistrar";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  RenderDisplayValueOptions,
} from "./types/CustomFieldPlugin";
import { createViewColumnsFromField } from "./utils/createViewColumnsFromField";
import { DEFAULT_INPUT_TYPE_IF_NO_VALUE, getConditionOperatorsByType } from "./utils/customFieldConstants";
import { extractAtMostOneTabularArrayOrThrow } from "./utils/extractAtMostOneTabularArrayOrThrow";
import { extractAtMostOneValueOrThrow } from "./utils/extractAtMostOneValueOrThrow";
import {
  getIsFieldInstanceFields,
  getIsFieldInstanceValueWithCrossWorkflowFields,
} from "./utils/fieldInstanceTypeguards";
import { getUpdateStartingConditionsInputsForEmptyOperators } from "./utils/getUpdateStartingConditionsInputsForEmptyOperators";
import { SafeMarkdown } from "./utils/SafeMarkdown";
import { validateRequired } from "./utils/validateBulkUploadCellValueUtils";

// TODO Misc: Turn the following into warnings and handle gracefully so the app doesn't crash in
// case of field misconfiguration.
const ERROR_INVALID_FIELD = "Provided 'text' field is invalid";
const WARNING_INVALID_FORM_VALUE = "Provided 'text'-field form value is not a valid string";
const ERROR_INVALID_VALUE_COUNT = "Provided 'text' field instance cannot have multiple values";
const ERROR_INVALID_VALUE_TYPE = "Provided 'text' field instance value must have type 'FieldInstanceValueString'";
const WARNING_UNEXPECTED_NULL_UNDEFINED_FORM_VALUE =
  "Provided 'text' field form value must not be null or undefined given the provided operator type";
const WARNING_INVALID_OPERATOR_TYPE = "Provided operator type is invalid for 'text' fields";
const WARNING_UNEXPECTED_DEFINED_FORM_VALUE =
  "Provided 'text' field form value must not be defined given the provided operator type";

function canProcessPropertyDataType(propertyDataType: PropertyDataType): boolean {
  return propertyDataType === "STRING";
}

function canProcessField(field: FieldFields): boolean {
  return (
    !field.isMultiValued && field.allowedValues.length === 0 && canProcessPropertyDataType(field.propertyType.dataType)
  );
}

type TextFieldPluginFrontendValue = string;
type TextFieldPluginType = CustomFieldPlugin<TextFieldPluginFrontendValue>;

const getConditionOperators: TextFieldPluginType["getConditionOperators"] = () => getConditionOperatorsByType("TEXT");

function renderDisplayValueInternal(
  fieldVersion: SpectrumFieldVersionFields,
  argsValue: string,
  options?: RenderDisplayValueOptions,
) {
  const isWorkflowEndReasonField =
    fieldVersion.field?.fieldType === "SYSTEM" &&
    fieldVersion.field?.name.toLocaleLowerCase() === WORKFLOW_END_REASON_FIELD_NAME.toLocaleLowerCase();

  // As of this writing, text fields do not support rich text — except for the Workflow End Reason
  // system field. For performance reasons in hot code paths like tables (see: SpectrumFormTable.tsx),
  // avoid converting values unless we know rich text is possible.
  const untruncatedValue = isWorkflowEndReasonField
    ? convertRichTextToPlainText(argsValue, { decodeEntities: true })
    : argsValue;

  // (clewis): Truncate large values before any rendering to avoid catastrophic performance.
  //
  // Multi-MB text values (e.g., API response JSON stored in text fields) block the main thread for
  // 10s of seconds when passed through react-markdown parsing, linkify regex scanning, and forced
  // reflows in RegrelloClampedText. By truncating before any rendering pipeline runs, we reduce the
  // parsing and DOM cost by orders of magnitude.
  const isTruncated = untruncatedValue.length > MAX_TEXT_FIELD_VALUE_CHARS;
  const truncatedValue = isTruncated
    ? stringEllipsize(untruncatedValue.slice(0, MAX_TEXT_FIELD_VALUE_CHARS))
    : untruncatedValue;

  // In tables, we have limited vertical space, so we don't want to respect line breaks to ensure
  // all table rows are the same height. But there are certain use cases where preserving multiple
  // consecutive whitespace characters is important for data consistency (e.g., part numbers with
  // specific formatting). So, first collapse any new lines to a single space, then render with
  // consecutive whitespace preserved and no text-wrapping.
  if (options?.context === "tableV2") {
    const collapsedNewLines = truncatedValue.replaceAll(/\n+/g, " ").replaceAll(/\s{2,}/g, " ");
    return (
      <RegrelloTooltippedText
        className="whitespace-pre truncate"
        tooltipProps={{
          contentProps: {
            className: "overflow-y-auto",
          },
          enableHoverableContent: true,
          side: "top",
        }}
      >
        {collapsedNewLines}
      </RegrelloTooltippedText>
    );
  }

  const markdownFieldConstraint = fieldVersion.fieldConstraints.find(
    (constraint) =>
      constraint.spectrumValueConstraint.valueConstraintRule === FrontendValueConstraintRuleName.MARKDOWN_FORMATTED &&
      constraint.constraintArgs[0] === "true",
  );

  // (clewis): Even if a field has the MARKDOWN_FORMATTED constraint, skip expensive react-markdown
  // + remark-gfm parsing when the value is clearly structured data (e.g., JSON) rather than actual
  // markdown. API responses and integration payloads are commonly stored in text fields and are
  // never intended to be rendered as markdown, but the field constraint may still be present.
  // Checking both the start and end of the trimmed value ensures we don't false-positive on values
  // that merely happen to start with `{` or `[` but aren't actually JSON.
  const trimmedValue = untruncatedValue.trim();
  const looksLikeJson =
    (trimmedValue.startsWith("{") && trimmedValue.endsWith("}")) ||
    (trimmedValue.startsWith("[") && trimmedValue.endsWith("]"));

  // Our SafeMarkdown component converts links, handles errors, and falls back to plain text. For
  // JSON-like values, skip markdown parsing since it's expensive for JSON-shaped data, particularly
  // large JSON data.
  const renderedContent =
    markdownFieldConstraint != null && !looksLikeJson ? (
      <RegrelloClampedText maxLines={options?.maxLines}>
        <SafeMarkdown>{truncatedValue}</SafeMarkdown>
      </RegrelloClampedText>
    ) : (
      <RegrelloLinkify>
        <RegrelloClampedText maxLines={options?.maxLines}>
          {looksLikeJson ? <span className="font-mono">{truncatedValue}</span> : truncatedValue}
        </RegrelloClampedText>
      </RegrelloLinkify>
    );

  if (isTruncated) {
    // Show a download button.
    return (
      // (clewis): min-w-0 is critical to ensure the text preview line wraps.
      // (clewis): mb-1 is to establish the same optical padding around the full value box.
      <div className="flex flex-col gap-2 min-w-0 mb-1">
        <div>{renderedContent}</div>
        <RegrelloCallout
          icon="warning"
          intent="warning"
          size="small"
          text={t`Please download the full value to view it.`}
          title={t`This value is too long to display in its entirety (> 10KB)`}
        />
        <RegrelloButton
          dataTestId={DataTestIds.TEXT_FIELD_DOWNLOAD_FULL_VALUE_BUTTON}
          intent="primary"
          onClick={() => {
            (options?.overrideDownloadTextAsFile ?? downloadFieldValueAsTextFile)(untruncatedValue, fieldVersion.name);
          }}
          size="x-small"
          startIcon="download"
          variant="surface"
        >
          {t`Download full value (.txt)`}
        </RegrelloButton>
      </div>
    );
  }

  return renderedContent;
}

const renderDisplayValueFromStringValue: TextFieldPluginType["renderDisplayValueFromStringValue"] = (
  fieldVersion,
  value,
  options,
) => {
  return renderDisplayValueInternal(fieldVersion, value, options);
};

const renderDisplayValue: TextFieldPluginType["renderDisplayValue"] = (fieldInstance, options = {}) => {
  const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);

  // Get the value, defaulting to empty string if null/undefined
  const rawValue = frontendFieldInstance.value ?? EMPTY_STRING;

  return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, rawValue, options);
};

const sortComparator: TextFieldPluginType["sortComparator"] = (
  fieldInstance1,
  fieldInstance2,

  direction = "asc",
): ComparatorResult => {
  if (direction === "desc") {
    return TextFieldPlugin.sortComparator(fieldInstance2, fieldInstance1, "asc");
  }

  if (fieldInstance1 == null) {
    return ComparatorResult.BEFORE;
  }

  if (fieldInstance2 == null) {
    return ComparatorResult.AFTER;
  }

  const fieldPlugin = CustomFieldPluginRegistrar.getPluginForField(fieldInstance1.field);

  const value1 = String(fieldPlugin.getValueForFrontend(fieldInstance1));
  const value2 = String(fieldPlugin.getValueForFrontend(fieldInstance2));

  return stringComparator(value1, value2);
};

/**
 * Describes a custom field that holds a short or long-form text value.
 */
export const TextFieldPlugin: TextFieldPluginType = {
  uri: "com.regrello.customField.text",
  version: "1.0.0",

  canProcessField: (field: FieldFields): boolean => {
    return canProcessField(field);
  },

  canProcessFieldInstance: (fieldInstance: FieldInstanceBaseFields): boolean => {
    try {
      translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
      return true;
    } catch (_error) {
      return false;
    }
  },

  canProcessPropertyDataType,

  findPropertyTypeIdFromLoadedPropertyTypeIds: (propertyTypes: PropertyTypeFields[]): number | undefined => {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING")?.id;
  },

  getColumnsForTable: createViewColumnsFromField,

  getConditionOperators,

  getCreateFieldInstanceValueInputsFromFormValue: (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, displayContext, sourceFieldInstanceId, spectrumFieldVersion, value } =
      inputs;
    if (!isValueValid(value)) {
      throw new Error(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { field, inputType, value }));
    }
    return {
      fieldId: field.id,
      stringValue: value ?? undefined,
      spectrumFieldVersionId: spectrumFieldVersion?.id,
      inputType,
      displayContext: displayContext ?? "DEFAULT",
      displayOrder,
      sourceFieldInstanceValueId: sourceFieldInstanceId,
    };
  },

  getCrossWorkflowSinksFieldInstanceIds: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number[] => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSinksFieldInstanceIds;
  },

  getCrossWorkflowSourceFieldInstanceIdFromValue: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSourceFieldInstanceId;
  },

  getFieldDisplayName: (): string => {
    return t`Text`;
  },

  getFilterDefinition: (_field: FieldFields) => {
    return getRegrelloDefaultFilterDefinitionStringValue();
  },

  getFilterDefinitionWithValues: (_field: FieldFields, filter: ViewFilterFields) => {
    const value = filter.value ?? EMPTY_STRING;
    return getRegrelloFilterDefinitionStringValue(filter.operator, value);
  },

  getEmptyValueForFrontend: (_, defaultValue): string => {
    return defaultValue || EMPTY_STRING;
  },

  getEmptyTabularValueForFrontend: (rowNumber: number): { value: string; rowNumber: number } => {
    return { value: EMPTY_STRING, rowNumber };
  },

  getIconName: () => {
    return "text-field";
  },

  getNameTemplateDisplayValueFromFormValue: (value) => {
    if (!isValueValid(value) || value == null || value === EMPTY_STRING) {
      return undefined;
    }
    return value;
  },

  getPreferredHomeTableColumnWidth: () => {
    return TableCellDefaultWidths.TEXT_CELL;
  },

  getSourceFieldInstance: (_fieldInstance: FieldInstanceFields): FieldInstanceFields | undefined => {
    return undefined;
  },

  getSourceFieldInstanceId: (fieldInstance: FieldInstanceBaseFields): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sourceFieldInstanceId;
  },

  getSourceFieldInstanceInputType: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  ): FieldInstanceValueInputType | undefined => {
    const sourceValue = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return sourceValue.sourceFieldInstanceInputType;
  },

  getSinkFieldInstanceIds: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sinksFieldInstanceIds;
  },

  getUpdateStartingConditionsInputsFromFormValues: (
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
  ): UpdateStartingConditionsInputs | undefined => {
    if (!isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        leftFieldInstance,
        operator,
      });
      return undefined;
    }

    if (operator === "EMPTY" || operator === "NOT_EMPTY") {
      if (value != null) {
        console.warn(WARNING_UNEXPECTED_DEFINED_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
      }
      return getUpdateStartingConditionsInputsForEmptyOperators(getFieldInstanceId(leftFieldInstance), operator);
    }

    if (value == null) {
      console.warn(WARNING_UNEXPECTED_NULL_UNDEFINED_FORM_VALUE, {
        leftFieldInstance,
        value,
      });
      return undefined;
    }
    if (typeof value !== "string") {
      console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
      return undefined;
    }

    return {
      leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
      operatorV2: operator,
      rightStringValue: value,
      rightFloatMultiValue: EMPTY_ARRAY,
      rightIntMultiValue: EMPTY_ARRAY,
      rightPartyIDMultiValue: EMPTY_ARRAY,
      rightStringMultiValue: EMPTY_ARRAY,
      rightTimeMultiValue: EMPTY_ARRAY,
      rightDateMultiValue: EMPTY_ARRAY,
    };
  },
  // (hchen): Currently this is only used for handling the edge case when flipping the field
  // instance input type from OPTIONAL to REQUESTED (i.e. required) when the a previously OPTIONAL
  // field is set as a native field on a future task. It's not necessary to implement for field
  // types other than date and user, since we only support those as native field now.
  getUpdateFieldInstanceValueInputsFromFieldInstance: (
    _fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): UpdateFieldInstanceValueInputs[] => {
    throw new Error("not implmented yet");
  },

  getValueForFrontend: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields): string => {
    return extractAtMostOneValueOrThrow({
      fieldInstance,

      fieldInstanceValueTypeName: "FieldInstanceValueString",
      errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
      errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
      getterIfNoValue: () => EMPTY_STRING,
      getterIfValue: (fieldInstanceValue) => fieldInstanceValue.stringValue ?? EMPTY_STRING,
    });
  },

  getTabularValueForFrontend: (fieldInstance: TableFieldInstanceFields) => {
    return translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
  },

  isCreateAndEditAllowed: true,

  isFeatureFlagEnabled: (): boolean => {
    return true;
  },

  isFieldInstanceEmpty: (fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues): boolean => {
    const { value } = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return value == null || value === EMPTY_STRING;
  },

  isFieldInstanceValueEqual: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    const frontendFieldInstance2 = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance2);
    return frontendFieldInstance.value === frontendFieldInstance2.value;
  },

  isFieldInstanceValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    if (frontendFieldInstance.sourceFieldInstanceId !== proposedChange.sourceFieldInstanceValueId) {
      return false;
    }

    if (frontendFieldInstance.inputType !== proposedChange.inputType) {
      return false;
    }

    return (
      (frontendFieldInstance.value == null && proposedChange.stringValue == null) ||
      (frontendFieldInstance.value == null && proposedChange.stringValue === EMPTY_STRING) ||
      frontendFieldInstance.value === proposedChange.stringValue
    );
  },

  isFieldInstanceTabularValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const rows = translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
    const originalRow = rows.find((row) => row.rowNumber === proposedChange.rowNumber);
    if (originalRow == null) {
      return false; // This potentially is a new row
    }

    return originalRow.value === proposedChange.stringValue;
  },

  renderDisplayValue: renderDisplayValue,

  renderDisplayValueFromStringValue: renderDisplayValueFromStringValue,

  renderFormField: <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    if (!canProcessField(field)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
    }
    const { operator } = props;
    const isDeleted = props.fieldInstance?.field.deletedAt != null;

    if (operator != null) {
      if (!isOperatorValid(operator)) {
        console.warn(WARNING_INVALID_OPERATOR_TYPE, {
          operator,
        });
        return undefined;
      }
      // (zstanik): Render single line text field for start conditions to avoid form fields getting
      // shifted down.
      return (
        <RegrelloControlledFormFieldText
          {...props}
          key={`${props.controllerProps.name}-${props.keySuffix}`}
          dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
          infoTooltipText={props.description}
          isDeleted={isDeleted}
        />
      );
    }

    return (
      <RegrelloControlledFormFieldTextMultiline
        {...props}
        key={`${props.controllerProps.name}-${props.keySuffix}`}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        infoTooltipText={props.description}
        isDeleted={isDeleted}
      />
    );
  },

  renderIcon: (props) => {
    return <RegrelloIcon {...props} iconName="text-field" />;
  },

  renderMultipleDisplayValuesForDataGrid: (fieldInstances, options) => {
    if (fieldInstances.length === 0) {
      return null;
    }

    if (fieldInstances.length === 1) {
      return renderDisplayValue(fieldInstances[0], { context: options?.context ?? "table" });
    }

    const instancesWithSource = fieldInstances.map((fieldInstance) => {
      return {
        content: (
          <RegrelloTooltippedText
            tooltipProps={{
              contentProps: { className: "overflow-y-auto" },
              enableHoverableContent: true,
              side: "top",
            }}
          >
            <RegrelloLinkify>
              {renderDisplayValue(fieldInstance, { context: options?.context ?? "table" })}
            </RegrelloLinkify>
          </RegrelloTooltippedText>
        ),
        workflowName: fieldInstance.workflow?.name,
        stageName: fieldInstance.actionItem?.workflowReference?.stageName,
        taskName: fieldInstance.actionItem?.name,
      };
    });

    return <RegrelloCustomFieldMultiValuePopover instancesWithSource={instancesWithSource} />;
  },

  sortComparator,

  validateBulkUploadCellValue: (_field, value, options) => {
    const normalizedString = value.trim();

    if (options.isRequired) {
      const result = validateRequired(normalizedString, options.isRequired);
      if (result != null) {
        return result;
      }
    }

    return {
      value: normalizedString,
    };
  },
};

interface FrontendTextFieldInstance {
  crossWorkflowSinksFieldInstanceIds: number[];
  crossWorkflowSourceFieldInstanceId: number | undefined;
  inputType: FieldInstanceValueInputType;
  name: string;
  sinksFieldInstanceIds: number[];
  sourceFieldInstanceId: number | undefined;
  sourceFieldInstanceInputType: FieldInstanceValueInputType | undefined;
  value: string | null;
}

function translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(
  fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
): FrontendTextFieldInstance {
  const { field } = fieldInstance;

  if (!canProcessField(field)) {
    throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
  }

  const isFieldInstanceFields = getIsFieldInstanceFields(fieldInstance);

  return extractAtMostOneValueOrThrow({
    fieldInstance,

    fieldInstanceValueTypeName: "FieldInstanceValueString",
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => ({
      name: field.name,
      inputType: DEFAULT_INPUT_TYPE_IF_NO_VALUE,
      crossWorkflowSinksFieldInstanceIds: EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: undefined,
      sinksFieldInstanceIds: EMPTY_ARRAY,
      sourceFieldInstanceId: undefined,
      sourceFieldInstanceInputType: undefined,
      value: null,
    }),
    getterIfValue: (fieldInstanceValue) => ({
      name: field.name,
      inputType: fieldInstanceValue.inputType,
      crossWorkflowSinksFieldInstanceIds:
        getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue) &&
        fieldInstanceValue.crossWorkflowSinksFieldInstanceValueString != null
          ? fieldInstanceValue.crossWorkflowSinksFieldInstanceValueString.map((value) => value.id)
          : EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue)
        ? fieldInstanceValue.crossWorkflowSourceFieldInstanceValueString?.id
        : undefined,
      sinksFieldInstanceIds: isFieldInstanceFields
        ? fieldInstanceValue.sinksFieldInstanceValueString?.map(({ id }) => id)
        : EMPTY_ARRAY,
      sourceFieldInstanceId: isFieldInstanceFields ? fieldInstanceValue.sourceFieldInstanceValueString?.id : undefined,
      sourceFieldInstanceInputType: isFieldInstanceFields
        ? fieldInstanceValue.sourceFieldInstanceValueString?.inputType
        : undefined,
      value: fieldInstanceValue.stringValue ?? null,
    }),
  });
}

function isOperatorValid(
  operator: ConditionOperator,
): operator is "EMPTY" | "NOT_EMPTY" | "EQUALS" | "NOT_EQUALS" | "CONTAINS_ANY_OF" | "CONTAINS_NONE_OF" {
  return getConditionOperators().find((stageStartOperator) => stageStartOperator.operator === operator) != null;
}

function isValueValid(value: unknown): value is TextFieldPluginFrontendValue | null {
  return value == null || typeof value === "string";
}

function translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance: TableFieldInstanceFields) {
  return extractAtMostOneTabularArrayOrThrow({
    fieldInstance,

    fieldInstanceValueTypeName: "FieldInstanceValueString",
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => EMPTY_ARRAY,
    getterIfTabularValue: (fieldInstanceValue) =>
      fieldInstanceValue.tabularValueString?.map((value) => ({
        rowNumber: value.rowNumber,
        value: value.value ?? EMPTY_STRING,
      })) ?? EMPTY_ARRAY,
  });
}

/** Sanitizes a field name for use as a filename and triggers a `.txt` download. */
function downloadFieldValueAsTextFile(content: string, fieldName: string) {
  const sanitizedName = fieldName.replace(/[^\w\- ]/g, "_");
  // (clewis): This is a filename that mustn't be translated.
  // eslint-disable-next-line lingui/no-unlocalized-strings
  downloadTextAsFile(content, `${sanitizedName}.txt`);
}
